import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  UserPlus,
  MessageSquare,
  Activity,
  AlertTriangle,
  Clock,
  Server,
  Database,
  Wifi,
  WifiOff,
  CheckCircle,
  XCircle,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  Bell,
  Share2,
  ArrowUpRight,
  Zap,
  Loader2,
  Calendar,
} from "lucide-react";
import { adminApi, AdminDashboard } from "../lib/api";

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 10000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
}

function trendPercent(
  current: number,
  previous: number,
): { value: string; direction: "up" | "down" | "flat" } {
  if (previous === 0 && current === 0)
    return { value: "0%", direction: "flat" };
  if (previous === 0) return { value: `+${current}`, direction: "up" };
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct > 0) return { value: `+${pct}%`, direction: "up" };
  if (pct < 0) return { value: `${pct}%`, direction: "down" };
  return { value: "0%", direction: "flat" };
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatMinutes(mins: number | null): string {
  if (mins === null) return "—";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
}

function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function daysAgoDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

// ─── StatCard ───────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  trend?: { current: number; previous: number };
  alert?: boolean;
  icon: React.ReactNode;
}

function StatCard({
  label,
  value,
  subtitle,
  trend,
  alert,
  icon,
}: StatCardProps) {
  const t = trend ? trendPercent(trend.current, trend.previous) : null;

  return (
    <div
      className={`bg-white rounded-2xl border p-5 ${alert ? "border-red-200 bg-red-50/50" : "border-slate-200"}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          {label}
        </span>
        <span className={alert ? "text-red-400" : "text-slate-300"}>
          {icon}
        </span>
      </div>
      <div className="flex items-end gap-2">
        <span
          className={`text-2xl font-bold ${alert ? "text-red-600" : "text-slate-900"}`}
        >
          {typeof value === "number" ? formatNumber(value) : value}
        </span>
        {t && (
          <span
            className={`text-xs font-semibold flex items-center gap-0.5 mb-0.5 ${
              t.direction === "up"
                ? "text-emerald-600"
                : t.direction === "down"
                  ? "text-red-500"
                  : "text-slate-400"
            }`}
          >
            {t.direction === "up" ? (
              <TrendingUp size={12} />
            ) : t.direction === "down" ? (
              <TrendingDown size={12} />
            ) : (
              <Minus size={12} />
            )}
            {t.value}
          </span>
        )}
      </div>
      {subtitle && <p className="text-xs text-slate-400 mt-1.5">{subtitle}</p>}
    </div>
  );
}

// ─── Skeleton Overlay ────────────────────────────────────────────────────────

/** Wraps a real StatCard and overlays an animated shimmer. Card keeps its real size. */
function SkeletonOverlay({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative">
      {/* Real card rendered invisibly to hold correct size */}
      <div className="invisible">{children}</div>
      {/* Shimmer overlay */}
      <div className="absolute inset-0 bg-white rounded-2xl border border-slate-200 p-5 animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="h-4 w-20 bg-slate-200 rounded" />
          <div className="h-4 w-4 bg-slate-100 rounded" />
        </div>
        <div className="flex items-end gap-2">
          <div className="h-8 w-14 bg-slate-200 rounded" />
          <div className="h-4 w-10 bg-slate-100 rounded mb-0.5" />
        </div>
        <div className="h-4 w-32 bg-slate-100 rounded mt-1.5" />
      </div>
    </div>
  );
}

// ─── Date Range Selector ────────────────────────────────────────────────────

type Preset = "7d" | "30d" | "90d" | "custom";

interface DateRangeSelectorProps {
  preset: Preset;
  from: string;
  to: string;
  onChange: (preset: Preset, from: string, to: string) => void;
}

function DateRangeSelector({
  preset,
  from,
  to,
  onChange,
}: DateRangeSelectorProps) {
  const presets: { key: Preset; label: string }[] = [
    { key: "7d", label: "7 days" },
    { key: "30d", label: "30 days" },
    { key: "90d", label: "90 days" },
    { key: "custom", label: "Custom" },
  ];

  const handlePreset = (key: Preset) => {
    if (key === "custom") {
      // Default custom to last 7 days
      onChange("custom", daysAgoDate(6), todayDate());
      return;
    }
    const days = key === "7d" ? 6 : key === "30d" ? 29 : 89;
    onChange(key, daysAgoDate(days), todayDate());
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex rounded-lg border border-slate-200 overflow-hidden">
        {presets.map((p) => (
          <button
            key={p.key}
            onClick={() => handlePreset(p.key)}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              preset === p.key
                ? "bg-slate-900 text-white"
                : "bg-white text-slate-500 hover:bg-slate-50"
            } ${p.key !== "7d" ? "border-l border-slate-200" : ""}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {preset === "custom" && (
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => onChange("custom", e.target.value, to)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-700"
          />
          <span className="text-xs text-slate-400">to</span>
          <input
            type="date"
            value={to}
            min={from}
            max={todayDate()}
            onChange={(e) => onChange("custom", from, e.target.value)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-700"
          />
        </div>
      )}
      {preset !== "custom" && (
        <span className="text-xs text-slate-400">
          {formatDateShort(from)} — {formatDateShort(to)}
        </span>
      )}
    </div>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<AdminDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState(0);

  // Date range state
  const [preset, setPreset] = useState<Preset>("7d");
  const [dateFrom, setDateFrom] = useState(daysAgoDate(6));
  const [dateTo, setDateTo] = useState(todayDate());
  const initialLoadDone = useRef(false);

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      try {
        const result = await adminApi.dashboard(dateFrom, dateTo);
        setData(result);
        setLastRefreshed(new Date());
        setSecondsAgo(0);
      } catch {
        // keep stale data on refresh failure
      } finally {
        setLoading(false);
        setPeriodLoading(false);
        setRefreshing(false);
        initialLoadDone.current = true;
      }
    },
    [dateFrom, dateTo],
  );

  // Initial fetch + auto-refresh every 60s
  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(), 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Countdown timer
  useEffect(() => {
    if (!lastRefreshed) return;
    const interval = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastRefreshed.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [lastRefreshed]);

  const handleDateChange = (p: Preset, from: string, to: string) => {
    setPreset(p);
    // If dates didn't change, no refetch needed (data is the same regardless of preset label)
    if (from === dateFrom && to === dateTo) return;
    setDateFrom(from);
    setDateTo(to);
    // Only show period skeletons if we already have data (not initial load)
    if (initialLoadDone.current) {
      setPeriodLoading(true);
    } else {
      setLoading(true);
    }
  };

  const rangeLabel =
    preset === "7d"
      ? "Last 7 days"
      : preset === "30d"
        ? "Last 30 days"
        : preset === "90d"
          ? "Last 90 days"
          : `${formatDateShort(dateFrom)} — ${formatDateShort(dateTo)}`;

  // Skeleton rows: render real StatCards (invisible) so grid rows match real card heights exactly
  const periodSkeletonRow1 = (
    <>
      <SkeletonOverlay>
        <StatCard
          label="Signups"
          value={0}
          trend={{ current: 0, previous: 0 }}
          subtitle={`vs previous ${rangeLabel.toLowerCase()}`}
          icon={<UserPlus size={18} />}
        />
      </SkeletonOverlay>
      <SkeletonOverlay>
        <StatCard
          label="Interactions"
          value={0}
          trend={{ current: 0, previous: 0 }}
          subtitle="vs previous period"
          icon={<Zap size={18} />}
        />
      </SkeletonOverlay>
      <SkeletonOverlay>
        <StatCard
          label="Contacts Created"
          value={0}
          trend={{ current: 0, previous: 0 }}
          subtitle="0 total"
          icon={<UserPlus size={18} />}
        />
      </SkeletonOverlay>
      <SkeletonOverlay>
        <StatCard
          label="Avg / User"
          value={0}
          subtitle="Interactions per active user (7d)"
          icon={<Activity size={18} />}
        />
      </SkeletonOverlay>
    </>
  );

  const periodSkeletonRow2 = (
    <>
      <SkeletonOverlay>
        <StatCard
          label="Referrals"
          value={0}
          trend={{ current: 0, previous: 0 }}
          subtitle="vs previous period"
          icon={<Share2 size={18} />}
        />
      </SkeletonOverlay>
      <SkeletonOverlay>
        <StatCard
          label="Notifications Sent"
          value={0}
          trend={{ current: 0, previous: 0 }}
          subtitle="vs previous period"
          icon={<Bell size={18} />}
        />
      </SkeletonOverlay>
      <SkeletonOverlay>
        <StatCard
          label="API Errors (5xx)"
          value={0}
          trend={{ current: 0, previous: 0 }}
          subtitle="Click to view details →"
          icon={<AlertTriangle size={18} />}
        />
      </SkeletonOverlay>
      <div />
    </>
  );

  const topicLabels: Record<string, string> = {
    bug_report: "Bug",
    feature_request: "Feature",
    account_issue: "Account",
    general: "General",
    other: "Other",
  };

  const statusColors: Record<string, string> = {
    open: "bg-blue-100 text-blue-700",
    in_progress: "bg-amber-100 text-amber-700",
    resolved: "bg-emerald-100 text-emerald-700",
    closed: "bg-slate-100 text-slate-600",
  };

  const cronLabels: Record<string, string> = {
    daily_health: "Daily Health Recalc",
    hard_delete: "Hard Delete Cleanup",
    notifications_hourly: "Hourly Notifications",
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-xs text-slate-400">
              Updated {secondsAgo < 5 ? "just now" : `${secondsAgo}s ago`}
            </span>
          )}
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">{periodSkeletonRow1}</div>
          <div className="grid grid-cols-4 gap-4">{periodSkeletonRow2}</div>
        </div>
      ) : data ? (
        <div className="space-y-10">
          {/* ═══════════════════════════════════════════════════════════════
              PERIOD ACTIVITY — date-filtered metrics
              ═══════════════════════════════════════════════════════════════ */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Calendar size={16} className="text-slate-400" />
                Period Activity
              </h2>
              <DateRangeSelector
                preset={preset}
                from={dateFrom}
                to={dateTo}
                onChange={handleDateChange}
              />
            </div>

            <div className="bg-slate-50/80 border border-slate-200/60 rounded-3xl p-5 space-y-5">
              {periodLoading ? (
                <>
                  <div className="grid grid-cols-4 gap-4">
                    {periodSkeletonRow1}
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    {periodSkeletonRow2}
                  </div>
                </>
              ) : (
                <>
                  {/* Row 1: Core activity metrics */}
                  <div className="grid grid-cols-4 gap-4 animate-fade-in-up">
                    <StatCard
                      label="Signups"
                      value={data.period.signups}
                      trend={{
                        current: data.period.signups,
                        previous: data.period.signupsPrevious,
                      }}
                      subtitle={`vs previous ${rangeLabel.toLowerCase()}`}
                      icon={<UserPlus size={18} />}
                    />
                    <StatCard
                      label="Interactions"
                      value={data.period.interactions}
                      trend={{
                        current: data.period.interactions,
                        previous: data.period.interactionsPrevious,
                      }}
                      subtitle={`vs previous period`}
                      icon={<Zap size={18} />}
                    />
                    <StatCard
                      label="Contacts Created"
                      value={data.period.contactsCreated}
                      trend={{
                        current: data.period.contactsCreated,
                        previous: data.period.contactsCreatedPrevious,
                      }}
                      subtitle={`${formatNumber(data.period.totalActiveContacts)} total`}
                      icon={<UserPlus size={18} />}
                    />
                    <StatCard
                      label="Avg / User"
                      value={data.period.avgInteractionsPerUser}
                      subtitle="Interactions per active user (7d)"
                      icon={<Activity size={18} />}
                    />
                  </div>

                  {/* Row 2: Referrals, Notifications, Errors */}
                  <div
                    className="grid grid-cols-4 gap-4 animate-fade-in-up"
                    style={{ animationDelay: "75ms" }}
                  >
                    <StatCard
                      label="Referrals"
                      value={data.period.referrals}
                      trend={{
                        current: data.period.referrals,
                        previous: data.period.referralsPrevious,
                      }}
                      subtitle="vs previous period"
                      icon={<Share2 size={18} />}
                    />
                    <StatCard
                      label="Notifications Sent"
                      value={data.period.notificationsSent}
                      trend={{
                        current: data.period.notificationsSent,
                        previous: data.period.notificationsSentPrevious,
                      }}
                      subtitle="vs previous period"
                      icon={<Bell size={18} />}
                    />
                    <button
                      onClick={() =>
                        navigate(`/errors?from=${dateFrom}&to=${dateTo}`)
                      }
                      className="text-left cursor-pointer"
                    >
                      <StatCard
                        label="API Errors (5xx)"
                        value={data.period.apiErrors}
                        trend={{
                          current: data.period.apiErrors,
                          previous: data.period.apiErrorsPrevious,
                        }}
                        alert={data.period.apiErrors > 0}
                        subtitle="Click to view details →"
                        icon={<AlertTriangle size={18} />}
                      />
                    </button>
                    <div />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              LIVE STATUS — always real-time, not date-filtered
              ═══════════════════════════════════════════════════════════════ */}
          <div>
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
              <Activity size={16} className="text-emerald-500" />
              Live Status
              <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                Real-time
              </span>
            </h2>

            <div className="space-y-8">
              {/* ── User Metrics ─────────────────────────────── */}
              <section>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Users size={14} /> User Metrics
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <StatCard
                    label="DAU"
                    value={data.users.dau}
                    trend={{
                      current: data.users.dau,
                      previous: data.users.dauPrevious,
                    }}
                    subtitle="Active today"
                    icon={<Users size={18} />}
                  />
                  <StatCard
                    label="WAU"
                    value={data.users.wau}
                    trend={{
                      current: data.users.wau,
                      previous: data.users.wauPrevious,
                    }}
                    subtitle="Last 7 days"
                    icon={<Users size={18} />}
                  />
                  <StatCard
                    label="MAU"
                    value={data.users.mau}
                    trend={{
                      current: data.users.mau,
                      previous: data.users.mauPrevious,
                    }}
                    subtitle="Last 30 days"
                    icon={<Users size={18} />}
                  />
                  <StatCard
                    label="Total Users"
                    value={data.users.totalUsers}
                    subtitle={`${formatNumber(data.users.guestVsAuth.auth)} auth · ${formatNumber(data.users.guestVsAuth.guest)} guest`}
                    icon={<Users size={18} />}
                  />
                </div>
              </section>

              {/* ── Support & System ─────────────────────────── */}
              <div className="grid grid-cols-2 gap-6">
                {/* Support */}
                <section>
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <MessageSquare size={14} /> Support
                  </h3>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <StatCard
                      label="Open"
                      value={data.support.openTickets}
                      icon={<MessageSquare size={16} />}
                    />
                    <StatCard
                      label="Needs Attention"
                      value={data.support.needsAttention}
                      alert={data.support.needsAttention > 0}
                      icon={<AlertTriangle size={16} />}
                    />
                    <StatCard
                      label="Avg Response"
                      value={formatMinutes(data.support.avgResponseTimeMinutes)}
                      icon={<Clock size={16} />}
                    />
                  </div>

                  {/* Recent tickets */}
                  {data.support.recentTickets.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <span className="text-xs font-semibold text-slate-500">
                          Recent Open Tickets
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {data.support.recentTickets.map((ticket) => (
                          <div
                            key={ticket.id}
                            className="px-4 py-3 flex items-center gap-3"
                          >
                            <span className="text-xs font-mono text-slate-400">
                              #{ticket.id}
                            </span>
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusColors[ticket.status] || "bg-slate-100 text-slate-600"}`}
                            >
                              {ticket.status.replace("_", " ")}
                            </span>
                            <span className="text-xs font-medium text-slate-600 truncate flex-1">
                              {ticket.userEmail}
                            </span>
                            <span className="text-[10px] text-slate-400 font-medium">
                              {topicLabels[ticket.topic] || ticket.topic}
                            </span>
                            <span className="text-[10px] text-slate-400">
                              {timeAgo(ticket.createdAt)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>

                {/* System */}
                <section>
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Server size={14} /> System Health
                  </h3>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <StatCard
                      label="DB Pool"
                      value={
                        data.system.dbPool
                          ? `${data.system.dbPool.active}/${data.system.dbPool.total}`
                          : "—"
                      }
                      subtitle={
                        data.system.dbPool
                          ? `${data.system.dbPool.idle} idle`
                          : "Unavailable"
                      }
                      icon={<Database size={16} />}
                    />
                    <StatCard
                      label="Push-Enabled"
                      value={data.push.enabledUsers}
                      subtitle={`of ${formatNumber(data.users.totalUsers)} total`}
                      icon={<Bell size={16} />}
                    />
                  </div>

                  {/* Redis */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      {data.system.redis?.connected ? (
                        <Wifi size={14} className="text-emerald-500" />
                      ) : (
                        <WifiOff size={14} className="text-red-400" />
                      )}
                      <span className="text-xs font-semibold text-slate-500">
                        Redis
                      </span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto ${
                          data.system.redis?.connected
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {data.system.redis?.connected ? "Connected" : "Down"}
                      </span>
                    </div>
                    {data.system.redis?.connected && (
                      <p className="text-xs text-slate-400">
                        Memory: {data.system.redis.memoryUsedMb} MB
                      </p>
                    )}
                  </div>

                  {/* Cron Jobs */}
                  <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <span className="text-xs font-semibold text-slate-500">
                        Cron Jobs
                      </span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {data.system.cronJobs.map((job) => (
                        <div
                          key={job.jobName}
                          className="px-4 py-3 flex items-center gap-3"
                        >
                          {job.status === "success" ? (
                            <CheckCircle
                              size={14}
                              className="text-emerald-500"
                            />
                          ) : job.status === "running" ? (
                            <Loader2
                              size={14}
                              className="text-amber-500 animate-spin"
                            />
                          ) : (
                            <XCircle size={14} className="text-red-500" />
                          )}
                          <span className="text-xs font-medium text-slate-700 flex-1">
                            {cronLabels[job.jobName] || job.jobName}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {formatDuration(job.durationMs)}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {job.lastRunAt ? timeAgo(job.lastRunAt) : "Never"}
                          </span>
                        </div>
                      ))}
                      {data.system.cronJobs.length === 0 && (
                        <div className="px-4 py-3 text-xs text-slate-400">
                          No job runs recorded
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              </div>

              {/* ── Growth ────────────────────────────────────── */}
              <section>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Share2 size={14} /> Growth
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="grid grid-cols-2 gap-3">
                    <StatCard
                      label="Total Referrals"
                      value={data.growth.totalReferrals}
                      icon={<Share2 size={16} />}
                    />
                    <StatCard
                      label="Conversion"
                      value={`${Math.round(data.growth.referralConversion * 100)}%`}
                      subtitle="Referred → active"
                      icon={<ArrowUpRight size={16} />}
                    />
                  </div>
                  {data.growth.topReferrers.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <span className="text-xs font-semibold text-slate-500">
                          Top Referrers
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {data.growth.topReferrers.map((ref, i) => (
                          <div
                            key={ref.name}
                            className="px-4 py-2.5 flex items-center gap-3"
                          >
                            <span className="text-xs font-bold text-slate-400 w-4">
                              {i + 1}.
                            </span>
                            <span className="text-xs font-medium text-slate-700 flex-1">
                              {ref.name}
                            </span>
                            <span className="text-xs font-bold text-slate-500">
                              {ref.count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <AlertTriangle size={48} className="text-red-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-600">
            Failed to load dashboard
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Check your connection and try again.
          </p>
          <button
            onClick={() => {
              setLoading(true);
              fetchData();
            }}
            className="mt-4 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
